import { Signal } from '@angular/core';

export type DeepUnsignal<T> =
  T extends Signal<infer V>
    ? DeepUnsignal<V>
    : T extends object
      ? { [K in keyof T]: DeepUnsignal<T[K]> }
      : T;

export function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (a == null || b == null) return false;
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  if (a instanceof Date || b instanceof Date) return false;
  if (a instanceof Map && b instanceof Map) return mapsEqual(a, b);
  if (a instanceof Map || b instanceof Map) return false;
  if (Array.isArray(a) && Array.isArray(b)) return arraysEqual(a, b);
  if (Array.isArray(a) || Array.isArray(b)) return false;

  if (typeof a !== 'object' || typeof b !== 'object') {
    return false;
  }

  return objectsEqual(a as Record<string, unknown>, b as Record<string, unknown>);
}

function mapsEqual(a: Map<unknown, unknown>, b: Map<unknown, unknown>): boolean {
  if (a.size !== b.size) return false;

  return [...a.entries()].every(([key, value]) => b.has(key) && deepEqual(value, b.get(key)));
}

function arraysEqual(a: unknown[], b: unknown[]): boolean {
  return a.length === b.length && a.every((value, index) => deepEqual(value, b[index]));
}

function objectsEqual(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);

  return (
    aKeys.length === bKeys.length &&
    aKeys.every((key) => Object.hasOwn(b, key) && deepEqual(a[key], b[key]))
  );
}
